'use client';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@ui/button';
import { Cell, Grid, Input, Dropdown, Radio } from '@web/atoms';
import { getYears, getMonths } from '../../pets/functions';
import { weightScreeningSchema } from '../../pets/schema';
import {
  ButtonWrapper,
  Label,
  RadioCellContainer,
  StyledPupForm,
  GridWrapper,
  Footnote,
  WeightInputContainer,
} from './styles';

export const PupInfoForm = ({
  labelName,
  labelBirthMonth,
  labelBirthYear,
  birthInfoFootnote,
  labelActivityLevel,
  noSelectionLabel,
  activityLevels,
  weightLabel,
  labelNeutered,
  neutered,
  submitButtonText,
  submitButtonVariant,
  onSubmit,
  defaultValues,
  isSubmitting,
}) => {
  const { control, handleSubmit, getValues } = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(weightScreeningSchema),
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const [optionsYear, setOptionsYear] = useState([]);
  const [optionsMonth, setOptionsMonth] = useState([]);

  const refreshYearList = () => {
    const year = getYears();
    setOptionsYear(year);
  };

  const refreshMonthList = () => {
    const yearValue = getValues('year');
    const month = getMonths(yearValue ?? 2024);
    setOptionsMonth(month);
  };

  useEffect(() => {
    refreshYearList();
  }, []);

  const onFormSubmit = event => handleSubmit(onSubmit)(event);

  return (
    <StyledPupForm onSubmit={onFormSubmit}>
      <GridWrapper>
        <Grid gap={{ row: 4, column: 4 }} columns={{ tablet: 1, mobile: 1 }}>
          <Cell columns={{ tablet: 2, mobile: 2 }}>
            <Controller
              control={control}
              name="name"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Input
                  {...field}
                  labelText={labelName}
                  status={error?.message ? 'error' : null}
                  error={error?.message}
                />
              )}
            />
          </Cell>
          <Cell columns={{ tablet: 2, mobile: 2 }}>
            <WeightInputContainer>
              <Controller
                control={control}
                name="weight"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Input
                    {...field}
                    type="number"
                    labelText={weightLabel}
                    value={field.value ?? ''}
                    status={error?.message ? 'error' : null}
                    error={error?.message}
                  />
                )}
              />
            </WeightInputContainer>
          </Cell>
          <Cell columns={{ tablet: 2, mobile: 2 }}>
            <Label>{labelActivityLevel}</Label>
            <Controller
              control={control}
              name="activity"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  label={labelActivityLevel}
                  options={activityLevels}
                  showLabel={false}
                  selected={field.value}
                  noSelectionLabel={noSelectionLabel}
                  error={error?.message}
                />
              )}
            />
          </Cell>
          <Cell columns={{ tablet: 2, mobile: 2 }}>
            <Grid
              gap={{ row: 2, column: 2 }}
              columns={{ tablet: 2, mobile: 2 }}
            >
              <Cell columns={{ tablet: 1, mobile: 1 }}>
                <Label>{labelBirthYear}</Label>
                <Controller
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      {...field}
                      showLabel={false}
                      noSelectionLabel={noSelectionLabel}
                      label={''}
                      onChange={e => {
                        field.onChange(e);
                        refreshMonthList();
                      }}
                      selected={field.value?.toString() ?? ''}
                      error={error?.message}
                      options={optionsYear}
                    />
                  )}
                  control={control}
                  name="year"
                />
              </Cell>
              <Cell columns={{ tablet: 1, mobile: 1 }}>
                <Label>{labelBirthMonth}</Label>
                <Controller
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      {...field}
                      showLabel={false}
                      noSelectionLabel={noSelectionLabel}
                      label=""
                      onChange={e => {
                        field.onChange(e);
                      }}
                      disabled={optionsMonth.length === 0}
                      selected={field.value?.toString() ?? ''}
                      error={error?.message}
                      options={optionsMonth}
                    />
                  )}
                  control={control}
                  name="month"
                />
              </Cell>
            </Grid>
            <Footnote>{birthInfoFootnote}</Footnote>
          </Cell>
          <Cell columns={{ tablet: 2, mobile: 2 }}>
            <Label>{labelNeutered}</Label>
            <RadioCellContainer>
              <Controller
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <>
                    <Radio
                      {...field}
                      choices={neutered}
                      legendText={labelNeutered}
                      variant={'light'}
                      error={error?.message}
                    />
                  </>
                )}
                control={control}
                name="spayedStatus"
              />
            </RadioCellContainer>
          </Cell>
        </Grid>
      </GridWrapper>
      <ButtonWrapper>
        <Button
          horizontalPadding={6}
          verticalPadding={4}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          variant={submitButtonVariant}
          type="submit"
        >
          {submitButtonText}
        </Button>
      </ButtonWrapper>
    </StyledPupForm>
  );
};
PupInfoForm.displayName = 'PupInfoForm';
PupInfoForm.propTypes = {
  labelName: PropTypes.string.isRequired,
  labelBirthMonth: PropTypes.string.isRequired,
  labelBirthYear: PropTypes.string.isRequired,
  birthInfoFootnote: PropTypes.string.isRequired,
  labelActivityLevel: PropTypes.string.isRequired,
  noSelectionLabel: PropTypes.string.isRequired,
  activityLevels: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  weightLabel: PropTypes.string.isRequired,
  labelNeutered: PropTypes.string.isRequired,
  neutered: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  submitButtonText: PropTypes.string.isRequired,
  submitButtonVariant: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isSubmitting: PropTypes.bool.isRequired,
};
